import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { useLiveActivity } from '../hooks/useLiveActivity';
import { getActivities } from '@firebasegen/default-connector';
import { activityName } from '../constants/common';
import { getMe, dataConnect } from '../firebaseConfig';
import { useAuth } from '../contexts/AuthContext';


const GlobalLiveActivityManager = () => {
    const { user, loading: authLoading } = useAuth();
    const { startActivity, isActive, updateActivity, endActivity } = useLiveActivity();

    const { data: activities } = useQuery({
        queryKey: ['allActivities'],
        queryFn: async () => {
            const { data } = await getActivities(dataConnect);
            return data?.activities || [];
        },
        enabled: !!user && !authLoading,
        refetchInterval: 60 * 1000,
        refetchOnWindowFocus: false
    });

    useEffect(() => {
        const checkAndStartActivity = async () => {
            if (!activities || activities.length === 0) {
                return;
            }
            const now = moment();

            try {
                // Process and Normalize all activities
                const processedActivities = activities.map(activity => {
                    const start = activity.startTimeZone
                        ? moment.tz(activity.startTimeTs, activity.startTimeZone)
                        : moment(activity.startTimeTs);
                    const end = activity.endTimeTs
                        ? (activity.endTimeZone ? moment.tz(activity.endTimeTs, activity.endTimeZone) : moment(activity.endTimeTs))
                        : start.clone().add(1, 'hour');

                    return {
                        ...activity,
                        normalizedStart: start,
                        normalizedEnd: end
                    };
                });

                const sorted = processedActivities.sort((a, b) => a.normalizedStart.diff(b.normalizedStart));

                // Find currently active activity
                const ongoingActivity = sorted.find(a => now.isBetween(a.normalizedStart, a.normalizedEnd, null, '[]'));

                // Helper: safe string name for an activity
                const safeActivityName = (act) => {
                    if (!act) return "";
                    const name = activityName(act);
                    return (typeof name === 'string') ? name : (act.name || "Activity");
                };

                const getLocName = (loc) => loc?.city || loc?.name || "";

                if (ongoingActivity) {
                    const currentAct = ongoingActivity;
                    const activeEvent = currentAct.event;
                    const currentIndex = sorted.indexOf(currentAct);
                    const nextAct = sorted.find(a => a.normalizedStart.isAfter(now));

                    // Progress: position across all activities (not within a single one)
                    const tourProgress = sorted.length > 1
                        ? (currentIndex / (sorted.length - 1))
                        : 0;

                    const venueName = activeEvent?.venue?.name || activeEvent?.name || "Event";

                    let cityHeader = activeEvent?.venue?.location?.city;
                    if (!cityHeader) {
                        const startCity = getLocName(currentAct.startLocation);
                        const endCity = getLocName(currentAct.endLocation);
                        cityHeader = (startCity && endCity) ? `${startCity} ➝ ${endCity}` : (endCity || startCity || "Tour");
                    }

                    const startLocName = getLocName(currentAct.startLocation);
                    const timeStr = currentAct.normalizedStart.format('HH:mm') + (startLocName ? `\n${startLocName}` : "");
                    const endTimeStr = currentAct.normalizedEnd.format('HH:mm');

                    let nextTimeStr = "";
                    if (nextAct) {
                        const nextLoc = getLocName(nextAct.location || nextAct.startLocation);
                        nextTimeStr = nextAct.normalizedStart.format('HH:mm') + (nextLoc ? `\n${nextLoc}` : "");
                    } else {
                        const endLocName = getLocName(currentAct.endLocation);
                        nextTimeStr = endLocName ? `Arrive\n${endLocName}` : "End";
                    }

                    const payload = {
                        tourName: String(activeEvent?.tour?.name || "Tourminator"),
                        eventDate: String(activeEvent?.date ? moment(activeEvent.date).format('MMM D') : ""),
                        totalActivities: String(sorted.length),
                        currentActivity: String(safeActivityName(currentAct)),
                        activityTime: String(timeStr),
                        activityTimeEnd: String(endTimeStr),
                        currentActivityStart: currentAct.normalizedStart.toISOString(),
                        currentActivityEnd: currentAct.normalizedEnd.toISOString(),
                        nextActivity: String(nextAct ? safeActivityName(nextAct) : "End of Event"),
                        nextActivityTime: String(nextTimeStr),
                        tourProgress: String(tourProgress),
                        activityType: String(currentAct.type || "general"),
                        venue: String(venueName),
                        location: String(cityHeader)
                    };

                    console.log("Global Manager: Synchronizing Live Activity...", payload.currentActivity);

                    if (isActive) {
                        await updateActivity(payload);
                    } else {
                        await startActivity(payload);
                    }
                } else {
                    // No ongoing activity — check for upcoming one (gap-filling)
                    const nextAct = sorted.find(a => a.normalizedStart.isAfter(now));

                    if (nextAct) {
                        // We're in a gap between activities — show "Break" with upcoming info
                        const activeEvent = nextAct.event;
                        const nextIndex = sorted.indexOf(nextAct);
                        const tourProgress = sorted.length > 1
                            ? ((nextIndex > 0 ? nextIndex - 1 : 0) / (sorted.length - 1))
                            : 0;

                        const venueName = activeEvent?.venue?.name || activeEvent?.name || "Event";
                        let cityHeader = activeEvent?.venue?.location?.city;
                        if (!cityHeader) {
                            const locName = getLocName(nextAct.startLocation || nextAct.location);
                            cityHeader = locName || "Tour";
                        }

                        const nextLoc = getLocName(nextAct.location || nextAct.startLocation);
                        const nextTimeStr = nextAct.normalizedStart.format('HH:mm') + (nextLoc ? `\n${nextLoc}` : "");

                        // Find the activity after next for the "next" slot
                        const afterNextAct = sorted.find(a => a.normalizedStart.isAfter(nextAct.normalizedStart));

                        const payload = {
                            tourName: String(activeEvent?.tour?.name || "Tourminator"),
                            eventDate: String(activeEvent?.date ? moment(activeEvent.date).format('MMM D') : ""),
                            totalActivities: String(sorted.length),
                            currentActivity: "Break",
                            activityTime: String(now.format('HH:mm')),
                            activityTimeEnd: String(nextAct.normalizedStart.format('HH:mm')),
                            currentActivityStart: now.toISOString(),
                            currentActivityEnd: nextAct.normalizedStart.toISOString(),
                            nextActivity: String(safeActivityName(nextAct)),
                            nextActivityTime: String(nextTimeStr),
                            tourProgress: String(tourProgress),
                            activityType: "break",
                            venue: String(venueName),
                            location: String(cityHeader)
                        };

                        console.log("Global Manager: Gap period — showing Break until", nextAct.normalizedStart.format('HH:mm'));

                        if (isActive) {
                            await updateActivity(payload);
                        } else {
                            await startActivity(payload);
                        }
                    } else {
                        // No upcoming activities at all — end the Live Activity
                        console.log("Global Manager: No upcoming activities — ending Live Activity");
                        if (isActive) {
                            await endActivity();
                        }
                    }
                }

            } catch (e) {
                console.error("GlobalLiveActivityManager: Sync error", e);
            }
        };

        checkAndStartActivity();

        // Re-check every 60s so the widget advances when time crosses activity boundaries
        const interval = setInterval(checkAndStartActivity, 60 * 1000);
        return () => clearInterval(interval);
    }, [activities, isActive, startActivity, updateActivity, endActivity]);

    return null;
};

export default GlobalLiveActivityManager;
